import React, { useState, useEffect, useCallback } from "react";
import { ValidationService } from "../../../services/ValidationService";
import { usePermissions } from "../../../PermissionsProvider";
import {
  ValidationRequest,
  ValidationRun,
} from "../../../model_gen/finetuning";
import RequestViewModal from "../../Request/RequestViewModal";
import { Card, CardContent } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import { 
  FileTextIcon,
  RocketIcon,
  CheckCircleIcon,
  Loader2Icon
} from "lucide-react";
import { cn } from "../../../lib/utils";
import ValidationResultsTable from "./ValidationResultsTable";

interface ValidationTabsProps {
  validationRunId?: string;
  finetuneRunId?: string;
}



const ValidationDetails: React.FC<ValidationTabsProps> = ({
  validationRunId,
  finetuneRunId,
}) => {
  const { apiKey } = usePermissions();
  const [validationRun, setValidationRun] = useState<ValidationRun>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resolvedValidationRunId, setResolvedValidationRunId] = useState<
    string | undefined
  >(validationRunId);
  const [selectedRequest, setSelectedRequest] =
    useState<ValidationRequest | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "FAILED":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "RUNNING":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      default:
        return "bg-muted text-foreground";
    }
  };

  // Fetch the latest validation run for the finetune run if validationRunId is not provided
  const fetchLatestValidationRun = useCallback(() => {
    if (!finetuneRunId || validationRunId) return;
    setIsLoading(true);
    ValidationService.getValidationRunsByFinetuneRunId(
      finetuneRunId,
      null,
      apiKey
    ).subscribe(
      (data: any) => {
        if (data && Array.isArray(data) && data.length > 0) {
          // Sort by date_created (descending) to get the most recent run
          const sortedRuns = [...data].sort(
            (a, b) => (b.date_created || 0) - (a.date_created || 0)
          );
          const latestRunId = sortedRuns[0]?.id;
          if (latestRunId) {
            setResolvedValidationRunId(latestRunId);
          }
        } else {
          setIsLoading(false);
          console.log("No validation runs found for this finetune run");
        }
      },
      (error: any) => {
        console.error("Error fetching validation runs:", error);
        setIsLoading(false);
      }
    );
  }, [apiKey, finetuneRunId, validationRunId]);

  useEffect(() => {
    if (!validationRunId && finetuneRunId) {
      fetchLatestValidationRun();
    } else {
      setResolvedValidationRunId(validationRunId);
    }
  }, [validationRunId, finetuneRunId, fetchLatestValidationRun]);

  const fetchData = useCallback(
    (showLoading: boolean = false) => {
      if (!resolvedValidationRunId) return;
      const loadingHandler = showLoading ? setIsLoading : null;
      console.log("Fetching validation requests for run ID:", resolvedValidationRunId);
      ValidationService.getValidationRequests(
        resolvedValidationRunId,
        loadingHandler,
        apiKey
      ).subscribe(
        (data: ValidationRun) => {
          setValidationRun(data);
        },
        (error: any) => {
          console.error("Error fetching validation requests:", error);
        }
      );
    },
    [apiKey, resolvedValidationRunId]
  );

  useEffect(() => {
    if (resolvedValidationRunId) {
      fetchData(true);
    }
  }, [apiKey, resolvedValidationRunId]);

  useEffect(() => {
    if (validationRun?.status?.toUpperCase() === "RUNNING") {
      const pollingInterval = 15000;
      const intervalId = setInterval(() => {
        fetchData(false);
      }, pollingInterval);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [validationRun?.status]);

  const handleViewRequest = (request: ValidationRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };





  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Stats Loading */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <Skeleton className="h-8 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table Loading */}
        <ValidationResultsTable
          requests={[]}
          isLoading={true}
          onViewRequest={() => {}}
        />
      </div>
    );
  }

  if (!resolvedValidationRunId) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="space-y-3">
          <h3 className="text-lg font-gc-med text-foreground">
            No validation run found
          </h3>
          <p className="text-muted-foreground max-w-md">
            {finetuneRunId
              ? "No validation runs available for this finetune run."
              : "Please provide a validation run ID or finetune run ID."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">Total Requests</p>
              </div>
              <p className="text-3xl font-gc-demi-bold text-foreground">
                {validationRun?.total_requests?.toLocaleString() || "0"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">Accuracy</p>
              </div>
              <p className="text-3xl font-gc-demi-bold text-foreground">
                {validationRun?.accuracy !== undefined
                  ? `${((validationRun?.accuracy || 0) * 100).toFixed(2)}%`
                  : "N/A"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <RocketIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">Status</p>
              </div>
              <div className="flex items-center gap-2">
                {validationRun?.status?.toUpperCase() === "RUNNING" && (
                  <Loader2Icon className="w-4 h-4 animate-spin text-blue-600" />
                )}
                <span className={cn(
                  "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                  getStatusColor(validationRun?.status || "")
                )}>
                  {validationRun?.status?.toUpperCase() || "N/A"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <ValidationResultsTable
        requests={validationRun.requests || []}
        isLoading={false}
        onViewRequest={handleViewRequest}
      />

      <RequestViewModal
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        apiKey={apiKey}
        requestId={selectedRequest?.request_id}
        applicationId={selectedRequest?.original_application_id?.toString()}
        applicationActionId={selectedRequest?.original_intent_id?.toString()}
      />
    </div>
  );
};

export default ValidationDetails;
